import React, { Component } from 'react';
export function ComponentsOfHardMillingSection() {
  return <section data-id="element-2362">
      <div className="bg-[#1874cd] py-4 px-6 rounded-t-xl" data-id="element-2363">
        <h2 className="text-2xl md:text-3xl font-bold text-white text-center" data-id="element-2364">
          Components of Hard Milling
        </h2>
      </div>
      <div className="bg-white p-8 rounded-b-xl shadow-sm border border-gray-200 border-t-0 space-y-12" data-id="element-2365">
        <p className="text-gray-700 text-lg leading-relaxed" data-id="element-2366">
          Hard milling uses a combination of several key technologies that can
          be categorized into components. These components are defined as the
          machine tool, the CAD/CAM system, the tool-holders, the cutting tools
          and the know-how. To be successful at hard milling, these components
          must come together in what can be called The Circle of Technology.
          Each component will be examined within the circle so that a clear
          understanding can be developed to facilitate successful hard milling
          of small- to medium- sized parts.
        </p>

        {/* Machine Tool */}
        <div className="grid md:grid-cols-2 gap-8 items-center pt-8 border-t border-gray-100" data-id="element-2367">
          <div className="space-y-4" data-id="element-2368">
            <h3 className="text-2xl font-bold text-[#0c79c8]" data-id="element-2369">Machine Tool</h3>
            <p className="text-gray-700 leading-relaxed" data-id="element-2370">
              Although all of the components of the circle are important, the
              machine tool component is, by far, the most significant. The
              machine must be designed for hard milling and have some of the
              same characteristics found in a HSM center. The base construction
              and the individual components of the machine such as the drive
              train, the spindle and the CNC control, etc., must be capable of
              handling all the demands of hard milling. The base construction of
              the machine must be extremely rigid and have a high degree of
              damping abilities. These characteristics are commonly found in
              machines constructed from polymer concrete. Machines constructed
              from polymer concrete typically have six to ten times the damping
              characteristics over machines constructed from cast iron. Polymer
              concrete also has excellent mechanical and thermal
              characteristics. The drive train should consist of digital drive
              technology for optimum acceleration and deacceleration. This
              technology provides the CNC control with the ability to perform a
              high degree of contouring accuracy encompassed with excellent
              dynamics capabilities. These characteristics will help minimize
              cutting tool wear.
            </p>
          </div>
          <div className="space-y-4" data-id="element-2371">
            <img src="https://drwzpk38qkpfb.cloudfront.net/www.peakedm.com/uploaded/images/Mikron/HSM/High%20Speed%20Machining%20Mill%20Construction.png800x800.png" alt="High Speed Machining Mill Construction" className="block w-full h-auto rounded-lg shadow-sm border border-gray-200" data-id="element-2372" />
            <p className="text-sm text-gray-600 italic" data-id="element-2373">
              High speed machining is usually associated with any spindle speed
              above 15k rpm, but it is much more than just a faster spindle. The
              whole machine must be considered when selecting a good candidate
              machine for HSM. Thermal compensation, overall machine rigidity
              and construction, positional feedback, the motion control system,
              tool retention, and many other characteristics must be looked at,
              in detail, before determining that a particular machine is
              suitable for the task.
            </p>
          </div>
        </div>

        {/* Spindle */}
        <div className="grid md:grid-cols-2 gap-8 items-center pt-8 border-t border-gray-100" data-id="element-2374">
          <div className="space-y-4 md:order-2" data-id="element-2375">
            <h3 className="text-2xl font-bold text-[#0c79c8]" data-id="element-2376">Spindle</h3>
            <p className="text-gray-700 leading-relaxed" data-id="element-2377">
              Another important aspect of the machine is its spindle. The
              spindle should be a vector-controlled motorized spindle.
              Vector-controlled spindles provide a great deal of flexibility,
              offering high torque at low rpms and maximum power for large speed
              range. The HSK interface between the tool holder and spindle
              should be used. This will provide minimal runout and excellent
              balancing at high speeds. Hybrid-ceramic bearing in the
              construction of the spindle will offer some excellent benefits
              including increased spindle stiffness, accuracy and temperature
              stability.
            </p>
          </div>
          <div className="md:order-1" data-id="element-2378">
            <img src="https://drwzpk38qkpfb.cloudfront.net/www.peakedm.com/uploaded/images/2022%20Machine%20Photos/Mikron%20Step-Tec%20Spindle%20Exploded%20Diagram.png800x800.png" alt="Mikron Step-Tec Spindle Exploded Diagram" className="block w-full h-auto rounded-lg shadow-sm border border-gray-200" data-id="element-2379" />
            <p className="text-sm text-gray-600 italic text-center mt-2" data-id="element-2380">
              Step-Tec High Speed Spindle from GF Machining Solutions
            </p>
          </div>
        </div>

        {/* CNC Control */}
        <div className="pt-8 border-t border-gray-100 space-y-4" data-id="element-2381">
          <h3 className="text-2xl font-bold text-[#0c79c8]" data-id="element-2382">CNC Control</h3>
          <p className="text-gray-700 leading-relaxed" data-id="element-2383">
            The performance of the CNC control is critical. A control with
            maximum block processing rate will ensure that the received data
            will be handled quickly and efficiently. This data should reside on
            a hard-drive located on the control. Incorporating numerical
            algorithm to calculate the velocity profile in the control will
            assist in smoothing machine motion. Additionally, all servo systems
            on a CNC machine exhibit a characteristic called servo lag.{' '}
            <strong data-id="element-2384">
              <em data-id="element-2385">Servo lag</em>
            </strong>{' '}
            is the actual amount that the machine position trails the position
            commanded by the control. In hard milling, any motion that is not
            continuous with the programmed path will create excessive stresses
            on the tool, causing the possibility of premature failure.
            Therefore, it is essential that the control have the ability to
            handle and control servo lag.
          </p>
        </div>

        {/* Toolholders */}
        <div className="grid md:grid-cols-2 gap-8 items-center pt-8 border-t border-gray-100" data-id="element-2386">
          <div className="space-y-4" data-id="element-2387">
            <h3 className="text-2xl font-bold text-[#0c79c8]" data-id="element-2388">Toolholders</h3>
            <p className="text-gray-700 leading-relaxed" data-id="element-2389">
              Continuing within the circle, tool-holders play an important role
              in hard milling. Because hard milling requires a large range of
              rpms-from low rpms for a roughing application to high rpm for
              HSM-only the HSK interface between the toolholder and spindle
              interface should be used. This will provide a very rigid and
              balanced tooling setup over the ISO taper interface.
            </p>
            <p className="text-gray-700 leading-relaxed" data-id="element-2390">
              The cutting tool can be held by several methods. These methods
              include collet chuck, hydraulic expansion, shrinkfit and power
              shrinking. The method selected should be determined by the
              requirements of the machining operation.
            </p>
          </div>
          <div data-id="element-2391">
            <img src="https://drwzpk38qkpfb.cloudfront.net/www.peakedm.com/uploaded/images/2022%20Machine%20Photos/HSK%20vs%20Steep%20Taper.png" alt="HSK vs Steep Taper" className="block w-full h-auto rounded-lg shadow-sm border border-gray-200" data-id="element-2392" />
          </div>
        </div>
        <div className="text-gray-700 leading-relaxed" data-id="element-2393">
          <p data-id="element-2394">
            Collet chucks are by far the most flexible. In addition to offering
            maximum flexibility, they are easy to handle, provide excellent
            shock absorbing characteristics and offer an excellent range of
            clamping diameters. These are suitable for aggressive roughing and
            semifinishing of hardened materials.
          </p>
          <p className="mt-4" data-id="element-2395">
            Hydraulic expansion toolholders also provide ease of use as well as
            high clamping forces and minimal runout, which will provide extended
            cutting tool life. However, hydraulic tooling can be expensive and
            bulky to use. Similar to collect chucks, hydraulic tooling is an
            excellent choice for roughing and semifinishing operations. For
            finishing those hardened cavities and cores with a high degree of
            accuracy and quality, power and heat-shrink toolholders provide
            excellent characteristics.
          </p>
        </div>

        {/* Cutting Tools */}
        <div className="pt-8 border-t border-gray-100 space-y-6" data-id="element-2396">
          <h3 className="text-2xl font-bold text-[#0c79c8]" data-id="element-2397">Cutting Tools</h3>
          <p className="text-gray-700 leading-relaxed" data-id="element-2398">
            Although hard milling uses many aspects of HSM, the selection of
            appropriate cutting tools is most important in hard milling.
            Furthermore, cutting tools are a significant cost factor in both
            hard milling and HSM; making a good choice can help save money. One
            of the main contributing factors of hard milling failure is the
            cutting tool. Many companies tend to skimp on selecting high-quality
            cutting tools, opting for less than adequate tools. To ensure that
            quality tools are selected, it is best to select an OEM who
            specializes in tools for hard milling or offers a well-defined
            product line for hard milling. The OEM should have technical staff
            on hand to assist in selecting the appropriate cutting tool for a
            particular hardened material and cutting strategy.
          </p>

          <div className="grid md:grid-cols-2 gap-8 items-center" data-id="element-2399">
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-2400">
              <p data-id="element-2401">
                For roughing hardened materials, four-flute end mills or higher
                are recommended. This will provide small chip loads while having
                the ability to cut at higher feedrates. Additionally, torus end
                mills are recommended for roughing because the sharp edges of
                conventional end mills are not sufficiently resistant against
                the possibilities of vibration and thermal stress when cutting
                hardened materials.
              </p>
              <p data-id="element-2402">
                The selection of cutting tools should be short with short flute
                lengths along with a helix angle of approximately thirty
                degrees. A thirty-degree helix has proven to be optimal for chip
                flow and dispersal of heat. The parent carbide substrate should
                also be considered. Only fine or ultrafine grain sintered
                carbides should be used. Sintered hard carbide is a composite
                material based on powder metallurgy. A binder (usually cobalt)
                is used to bond carbide particles. Tungsten-, titanium-, tantal-
                or niobcarbide are the most used elements and provide the
                required hardness at high temperatures and wear resistance. With
                a reduction of the grain size of the carbide particles to about
                0.5 to 0.6fm, the edge strength can be further increased, while
                the tendency to adhesion can be reduced. For larger hardened
                cavities and cores, a selection of inserted cutting tools should
                be considered. Carbide inserts are less expensive than end mills
                and by rotating the insert, insert life can be extended.
                However, these tools are not typically designed for high spindle
                speeds and runout can be significant. There is also a
                significant safety risk if improper handling occurs.
              </p>
            </div>
            <div data-id="element-2403">
              <img src="https://drwzpk38qkpfb.cloudfront.net/www.peakedm.com/uploaded/images/2022%20Machine%20Photos/HSM_Tool_Explained%20Hard%20Milling%20Cutting%20Tools.png" alt="Hard Milling Cutting Tools" className="block w-full h-auto rounded-lg shadow-sm border border-gray-200" data-id="element-2404" />
            </div>
          </div>

          <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-2405">
            <p data-id="element-2406">
              Hard milling creates a great amount of stress on the tool from
              high heat and abrasive wear. To help overcome these stresses,
              coatings must be applied to the cutting tool. These coatings offer
              a protective layer on the tool, substantially increasing its life.
              The most common coatings are titanium nitride (TiN), titanium
              carbon nitride (TiCN), titanium aluminum nitride (TiAlN) and
              titanium aluminum carbon nitride (TiAlCN); each coating has its
              benefits. Coating selection should be made based on individual
              properties and the OEM may dictate these when selecting a cutting
              tool.
            </p>
            <p data-id="element-2407">
              The titanium-based hard material layers such as TiCN and TiAlN are
              the most commonly used protection layers for HSM and hard milling
              cutting tools. The resistance to wear (hardness) is the most
              important property of TiCN, while TiAlN has a better heat and
              oxidation resistance property. The OEM may also further enhance
              the coatings by offering unique blends, perhaps creating a leading
              edge over coating quality and tool life. Recently, other advances
              in coatings have entered the market such as proprietary coatings.
              Commonly called Rainbow coating, it is a proprietary multielement
              PVD (physical vapor deposit) coating offering a competitive edge
              over traditional nonpropriety coatings.
            </p>
            <p data-id="element-2408">
              Flood coolants are traditionally used throughout the machining
              process to help disperse heat and remove chips from the work area
              and the cutting tool. Hard milling often generates a tremendous
              amount of heat over conventional machining. This heat transferred
              into the chip and the use of flood coolant during hard milling
              causes the coolant to vaporize as it hits the hot chips. The use
              of coolants also can create thermal instability with the tool.
              Therefore, flood coolants are not commonly used in hard milling.
              To help displace chips during the cutting process, compressed air
              is used. Additionally, a combination of oil/mist is often
              selected. The addition of oil helps reduce friction, therefore
              increasing tool life and surface finish. When using oil/mist, an
              oil/mist extraction unit should be integrated into the machine
              tool to help remove the oil from the air.
            </p>
          </div>
        </div>

        {/* CAD/CAM */}
        <div className="pt-8 border-t border-gray-100 space-y-6" data-id="element-2409">
          <h3 className="text-2xl font-bold text-[#0c79c8]" data-id="element-2410">CAD/CAM</h3>
          <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-2411">
            <p data-id="element-2412">
              Falling in line, the CAD/CAM system is another equally important
              component. Today's CAD/CAM systems have greatly advanced over the
              years, providing a wide variety of system tools and capabilities.
              Furthermore, development continues to grow at an outstanding pace.
              However, not all of today's systems are created equal and it is
              important to realize that there are still many systems that do not
              have the built-in tools or strategies to create toolpaths for hard
              milling or HSM.
            </p>
            <p data-id="element-2413">
              Although no system is devoted entirely to hard milling, many that
              offer HSM capabilities will have the same strategies for hard
              milling since there is a common relationship between the two
              technologies. When considering hard milling, strategies that keep
              the tool in motion should be used for hard milling. This will
              ensure that the tool is continuously cutting with a constant chip
              load. This is one of the more desirable conditions to maintain for
              hard milling. Before further discussion on machining strategies
              can begin, a careful review of the CAD model is important.
            </p>
            <p data-id="element-2414">
              One of the common problems associated with CAD/CAM programming is
              the model. Many companies import data from other systems using a
              variety of importing tools. In particular, job shops may deal with
              various clients, who are using a variety of different CAD systems.
              In this case, a file transfer format needs to be defined to
              transfer geometry data from the client CAD system to the CAM
              system. In order to avoid time-consuming repairs of the model, it
              is very important to select the proper file format for a data
              transfer. Some CAM systems offer special interface options to
              directly read in file formats from other CAM systems. When data is
              imported, problems can pop up. These problems range from missing
              trimmed surfaces to bad solid models causing numerous headaches
              for creating efficient toolpaths. These problems need to be fixed
              before developing toolpaths.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center" data-id="element-2415">
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-2416">
              <p data-id="element-2417">
                Poorly developed models also are a common problem. Typically,
                how a model is created is going to dictate what machining
                techniques are used. If machining strategies are not considered
                during the development of the model, then the programmer may not
                be able to use certain toolpath strategies. Without these
                considerations, hard milling a cavity or core may not always be
                suitable without modifying the model.
              </p>
              <p data-id="element-2418">
                Before toolpaths can be applied, there must be a complete
                analysis of the part. Not all parts are suitable for hard
                milling. The specific areas to be machined should be clearly
                identified, determining the smallest internal radius and largest
                working depth. A 4:1 ratio of length to tool diameter commonly
                does not pose any problems. Problems arise when the ratio grows,
                and careful consideration should be made towards the feasibility
                of success. When ratios are excessive, experience at hard
                milling will have an important role in determining how
                successful one will be. Hard milling with tool diameters as
                small as 0.005" can be accomplished as long as care is taken to
                maintain a constant chip load and machining at minimal cutting
                depths. These depths can commonly range from 0.0002" to 0.0005"
                on such small tools. Toolpath strategies can now be determined.
              </p>
            </div>
            <div className="space-y-4" data-id="element-2419">
              <img src="https://drwzpk38qkpfb.cloudfront.net/www.peakedm.com/uploaded/images/2022%20Machine%20Photos/CAMplete%20TruePath%20Post%20Processor.png800x800.png" alt="CAMplete TruePath Post Processor" className="block w-full h-auto rounded-lg shadow-sm border border-gray-200" data-id="element-2420" />
              <p className="text-sm text-gray-600 italic" data-id="element-2421">
                CAMplete TruePath is an easy-to-use application that takes you
                from your CAM System to your machine efficiently and safely.
                Using toolpath data from all industry leading CAM Systems,
                TruePath's customizable post-processor allows you to output your
                NC programs the way you need, with no on-machine editing
                required. 5-axis simulation, visualization and verification
                tools give you the confidence of knowing how your machine will
                behave before you run a single line of G-code.
              </p>
            </div>
          </div>

          <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-2422">
            <p data-id="element-2423">
              As mentioned earlier, in hard milling it is important to keep the
              tool in motion avoiding dramatic changes in direction. Therefore,
              depending on the complexity of the part, multiple toolpath
              strategies may be required to complete the part. The process of
              recognizing and separating key areas of the part and applying
              different toolpath strategies is commonly called modular toolpath
              programming (MTP). This method of programming is generally used in
              HSM to maintain high cutting speeds. Similarly, MTP can be used to
              help keep the tool in motion while avoiding dramatic changes in
              direction Toolpath quality is commonly overlooked in a CAM system.
              Figure 11 represents what appears to be a normal looking toolpath;
              but upon further evaluation, it is revealed that there are many
              unnecessary changes in the toolpath direction.
            </p>
            <p data-id="element-2424">
              Stepping back to our machine tool, builders have incorporated
              elaborate acceleration and deceleration servo tuning algorithms as
              well as complex servo lag algorithms (look ahead features) into
              their controls to enhance motion control. These look ahead or
              control feedrates by analyzing directional changes within the NC
              code. The greater the directional change (for example, zero to
              ninety degrees) the more the control has to slow down to maintain
              the programmed path. In hard milling, these abrupt changes in
              toolpath direction create dwells and slow downs, which can have an
              effect on tool life and surface finish. Therefore, toolpath
              quality should be an important feature of your CAM system.
            </p>
            <p data-id="element-2425">
              Programming errors have a tendency to be less forgiving when
              conventional machining techniques and softer materials are used.
              With hard milling and HSM, programming error will no doubt have
              severe consequences if not caught in time. Cutting tools can
              easily be broken; tool holders, fixtures and even the machine can
              be damaged costing hundreds to thousands of dollars. Personal
              safety also can be at risk. To ensure programming errors are
              caught before they happen, the CNC code should be thoroughly
              reviewed for errors. Most CAD/CAM systems incorporate some type of
              toolpath verification or toolpath simulation within their
              software. Unfortunately, many of them only view the intermediate
              file rather than the posted NC code or the C/L toolpath file where
              errors can occur. Therefore, care should be taken to ensure that
              the posted NC code is reviewed for errors. If your CAD/CAM system
              does not have the tools to view or simulate the NC code directly,
              there are numerous software packages on the market that will.
              These products can range from a few hundreds dollars to several
              thousands of dollars but they will save you many major problems by
              eliminating potential crashes and safety issues at the machine.
            </p>
          </div>
        </div>

        {/* Know-how */}
        <div className="pt-8 border-t border-gray-100 space-y-4" data-id="element-2426">
          <h3 className="text-2xl font-bold text-[#0c79c8]" data-id="element-2427">Know-how</h3>
          <p className="text-gray-700 leading-relaxed" data-id="element-2428">
            Finally, proper know-how and training are vital keys to being
            successful at hard milling. You can have all of the above elements
            but they are no good to you without a clear understanding of
            principle processing procedures. Often, an entirely new approach is
            required to gain the profitable advantages of the hard milling
            process over conventional milling. The successful employment of the
            hard milling process is based on specific know-how, advanced
            knowledge of the basics of the HSM process, choice of appropriate
            cutting tools, choice of appropriate clamping systems for cutting
            tools (and parts) and professionalism, using an HSM capable CAD/CAM
            system.
          </p>
        </div>
      </div>
    </section>;
}